/** @jsx jsx */

import { useMemo } from 'react';
import { connect } from 'react-redux';

import { Badge } from '@zapier/design-system';
import { Profile } from '@zapier/shared-entities';
import { ensureTrailingSlash } from '@zapier/url-utils';
import { jsx } from '@emotion/core';

import ByCliToolMessage from 'app/developer-v3/components/ByCliToolMessage';
import PendingVersionLifecycleMessage from 'app/developer-v3/components/PendingVersionLifecycleMessage';
import { ReviewStatus } from 'app/developer-v3/types/statuses';
import {
  CurrentVersionReadOnlyReason,
  useCurrentVersionReadOnlyReason,
} from 'app/developer-v3/hooks';

import Menu from './Menu';
import { SidebarManageKey } from './types';
import { isVersionlessRoute, buildSidebarManage } from './utils';

type Props = {
  baseUrl: string;
  currentUrl: string;
  hasIssues: boolean;
  isLifecyclePending: boolean;
  isPrivate: boolean;
  isPublicIntention: boolean;
  isPublished: boolean;
  isTeamEnabled: boolean;
  issuesCount: number;
  reviewStatus: ReviewStatus;
  versionlessUrl: string;
  appId: number;
};

function SectionManage({
  baseUrl,
  currentUrl,
  issuesCount,
  hasIssues,
  isLifecyclePending,
  isPrivate,
  isPublicIntention,
  isPublished,
  isTeamEnabled,
  reviewStatus,
  versionlessUrl,
}: Props) {
  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const isNotAdmin =
    currentVersionReadOnlyReason === CurrentVersionReadOnlyReason.NOT_ADMIN;

  const finalizeItem = ({ key, name, section }) => {
    const url = `${isVersionlessRoute(key) ? versionlessUrl : baseUrl}/${key}`;

    const item = {
      badge:
        key === SidebarManageKey.issues && issuesCount ? (
          <Badge>{`${issuesCount}`}</Badge>
        ) : undefined,
      label: name,
      url,
      isActive: ensureTrailingSlash(currentUrl).startsWith(`${url}/`),
      /** Disable the publish button when the app is being migrated */
      isEnabled:
        key === SidebarManageKey.publishing ? !isLifecyclePending : true,
      section,
    };

    return { key, item };
  };

  const menuItems = useMemo(() => {
    return buildSidebarManage({
      hasIssues,
      isNotAdmin,
      isPrivate,
      isPublicIntention,
      isPublished,
      isTeamEnabled,
      reviewStatus,
    });
  }, [
    hasIssues,
    isNotAdmin,
    isPrivate,
    isPublicIntention,
    isPublished,
    isTeamEnabled,
    reviewStatus,
  ]);

  return (
    <Menu
      disabledMessage={
        isLifecyclePending ? (
          <PendingVersionLifecycleMessage />
        ) : (
          <ByCliToolMessage theme="dark" />
        )
      }
      hasTopBorder={true}
      itemsProps={menuItems.map(finalizeItem)}
      menuKey="manage"
      title="Manage"
      titleIconName="miscList"
    />
  );
}

const mapStateToProps = state => ({
  isTeamEnabled: Profile.selectors.isSwitchOn(
    'enable_manage_team_tab',
    Profile.selectors.all.currentProfile(state)
  ),
});

export default connect(mapStateToProps)(SectionManage);
